import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import TypographyTsoPq from '@components/Typography/TypographyTsoPq';
import { Box, styled, Tooltip, TooltipProps } from '@mui/material';
import { t } from 'i18next';

type TooltipTsoPqProps = TooltipProps & {
  variant?: 'default' | 'legend';
  multiline?: boolean;
  customWidth?: string;
};

const StyledTooltip = styled(({ variant, multiline = false, customWidth, ...props }: TooltipTsoPqProps) => (
  <Tooltip {...props} classes={{ popper: props.className }} />
))<TooltipTsoPqProps>(({ variant, multiline, customWidth }) => ({
  '& .MuiTooltip-tooltip': {
    fontFamily: tsoPqTheme.typography.openSans,
    fontSize: tsoPqTheme.typography.fontsize_body_body_small,
    lineHeight: tsoPqTheme.typography.lineHeight_s,
    fontWeight: 400,
    padding: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_12,
    backgroundColor: tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_enabled,
    color: tsoPqTheme.palette.customTextColor.color_font_ft_white,
    borderRadius: '4px',
    display: 'flex',
    flexDirection: variant === 'legend' ? 'column' : 'row',
    gap: '0.5rem',
    minWidth: customWidth ?? (multiline ? '7.5rem' : '5rem'),
  },
}));

export const StyledMultilineTooltipContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
}));
export const StyledTooltipLine = styled('span')(() => ({
  fontFamily: tsoPqTheme.typography.openSans,
  fontSize: tsoPqTheme.typography.fontsize_body_body_small,
  lineHeight: tsoPqTheme.typography.lineHeight_s,
  fontWeight: 400,
  color: tsoPqTheme.palette.customTextColor.color_font_ft_white,
}));

const StyledLegendaryItemContainer = styled(Box)(() => {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: tsoPqTheme.spacingValues.buttonSpacing.spacing_button_large_iconbutton_lg_padding,
  };
});

interface StyledLegendColoredBoxProps {
  color: string;
}

const StyledLegendColoredBox = styled(Box)<StyledLegendColoredBoxProps>(({ color }) => ({
  width: '1.0625rem',
  height: '1.0625rem',
  backgroundColor: color,
  borderRadius: '0.125rem',
}));

export const LegendaryItem = ({ color, label }: { color: string; label: string }) => (
  <StyledLegendaryItemContainer>
    <StyledLegendColoredBox color={color} />
    <TypographyTsoPq typographyType="labelMD" color={tsoPqTheme.palette.customTextColor.color_font_ft_white}>
      {label}
    </TypographyTsoPq>
  </StyledLegendaryItemContainer>
);

const LegendTooltipContent = () => (
  <>
    <LegendaryItem
      color={tsoPqTheme.palette.customInformation.color_signal_success_success}
      label={t('info.legend.green')}
    />
    <LegendaryItem
      color={tsoPqTheme.palette.customInformation.color_signal_warning_warning}
      label={t('info.legend.orange')}
    />
    <LegendaryItem color={tsoPqTheme.palette.customError.color_signal_error_error} label={t('info.legend.red')} />
    <LegendaryItem
      color={tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_dark}
      label={t('info.legend.gray')}
    />
  </>
);

const TooltipTsoPq = ({
  children,
  title,
  variant = 'default',
  multiline = false,
  customWidth,
  ...props
}: TooltipTsoPqProps) => {
  let content: React.ReactNode;

  if (variant === 'legend') {
    content = <LegendTooltipContent />;
  } else if (multiline && typeof title === 'string') {
    content = (
      <StyledMultilineTooltipContainer>
        {title.split('\n').map((line, index) => (
          <StyledTooltipLine key={`${line}-${index}`}>{line}</StyledTooltipLine>
        ))}
      </StyledMultilineTooltipContainer>
    );
  } else {
    content = title;
  }

  return (
    <StyledTooltip
      title={content}
      placement="bottom-end"
      disableInteractive
      customWidth={customWidth}
      variant={variant}
      {...props}
    >
      {children}
    </StyledTooltip>
  );
};

export default TooltipTsoPq;
